<template>
    <Page behind-nav class="faq-page">
        <div class="resources-hero">
            <div class="container container--center">
                <h1 class="resources-hero__title text-heading text-heading--md">{{ $t("support.faq_subheading") }}</h1>
                <form class="resources-hero__search-form" role="search" @submit.prevent="blurSearch">
                    <ProInput
                        labelHidden
                        @input="performSearch"
                        :value="query"
                        ref="search"
                        :label="$t('support.faq_page_search_placeholder')"
                        :placeholder="$t('support.faq_page_search_placeholder')"
                        class="hide-label"
                    >
                        <template v-slot:after>
                            <ProIcon icon="search" />
                        </template>
                    </ProInput>
                </form>
                <p
                    class="resources-hero__search-results"
                    :class="{ 'resources-hero__search-results--active': query && query.length > 1 }"
                    aria-live="polite"
                    :aria-hidden="!query || query.length < 1"
                >
                    {{ $tc("support.faq_answers_found", resultsCountTotal) }}
                </p>
            </div>
        </div>
        <div class="container resources-section" v-for="product in products" v-if="product.questions.length">
            <h2 class="resources-section__title text-heading text-heading">
                {{ product.name }}
            </h2>
            <div :id="question.id" class="resources-column-layout resources-column-layout--faq" v-for="question in product.questions" :key="question.dataId">
                <h3 class="resources-column-layout__title text-heading text-heading--xs">
                    <span v-html="question.title"></span>&nbsp;
                    <a v-if="question.id" :href="`#${question.id}`" tabindex="-1" aria-hidden="true" @click="clipboardCopy(question.id)">
                        <ProIcon icon="copy-link" />
                    </a>
                </h3>
                <div class="resources-column-layout__body" v-html="markdownToHtml(question.answer)"></div>
            </div>
        </div>
        <div class="resources-footer">
            <div class="container container--narrow">
                <h2 class="resources-footer__title text-heading text-heading--md">{{ $t("faq.nr_title") }}</h2>
                <p class="resources-footer__body text-body">{{ $t("faq.nr_body") }}</p>
                <ProButton pill variant="primary" href="https://procreate.com/support?contact=true">{{ $t("faq.nr_cta") }}</ProButton>
            </div>
        </div>
        <Alert ref="copyFailed" title="Unable to copy to clipboard" :action-title="$t('global.okay')" />
    </Page>
</template>

<script>
import Alert from "@/components/Alert";
import MarkdownIt from "markdown-it";
import ProInput from "@/components/ProInput";
import _, { debounce } from "underscore";
import { mapGetters } from "vuex";
import { metaTitles, metaDescriptions, metaImages } from "~/utils/meta";
import { copyToClipboard } from "~/utils";

const md = new MarkdownIt({ html: true });

export default {
    components: { ProInput, Alert },

    data() {
        return {
            query: "" || this.$route.query.q,
        };
    },

    computed: {
        ...mapGetters({
            products: "faq/getProductResults",
        }),

        resultsCountTotal() {
            return this.products.length ? this.products.reduce((count, product) => count + product.questions.length, 0) : 0;
        },
    },

    methods: {
        performSearch: _.debounce(function (query) {
            this.query = query;
            this.$store.commit("faq/setQuery", query);
        }, 300),

        markdownToHtml(string) {
            return md.render(string);
        },

        blurSearch(event) {
            this.$refs.search.blur();
        },

        async clipboardCopy(id) {
            if (process.client) {
                let copied = await copyToClipboard(`${window.location.origin}${this.$route.path}#${id}`); //adds current url + anchor to clipboard
                if (copied == false) {
                    this.$refs.copyFailed.open();
                }
            }
        },
    },

    watch: {
        query(value) {
            this.$router.replace({ query: value.length ? { q: value } : null });
        },
        "$i18n.locale": function () {
            this.$store.dispatch("faq/fetchPage");
        },
    },

    async asyncData({ store, route, error }) {
        try {
            await store.dispatch("faq/fetchPage");
        } catch (e) {
            if (e.response?.status === 404) {
                return error({ statusCode: 404 });
            }

            throw e;
        }
    },

    created() {
        if (this.$route.query.q) {
            this.$store.commit("faq/setQuery", this.$route.query.q);
        }
    },

    head() {
        const i18nHead = this.$nuxtI18nSeo();
        return {
            title: this.$t("faq.meta.title"),
            meta: [
                ...metaTitles(this.$t("faq.meta.title")),
                ...metaDescriptions(this.$t("faq.meta.description")),
                ...metaImages(this.$asset(this.$t("faq.meta.image"))),
                ...i18nHead.meta,
            ],
            link: [...i18nHead.link],
        };
    },
};
</script>
